import { useCallback, useEffect, useMemo } from "react"

import { getYear } from "date-fns"

import { useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useUrlParams } from "@l2r-front/l2r-utils"

import { ROADSIDE_CONDITION_URL_PARAM, ROADSIDE_TAGS_URL_PARAM, ROADSIDE_TYPE_URL_PARAM } from "../../constants/urlParams"
import { useRoadsideAscendantCodes } from "../../hooks/utils/useRoadsideAscendantCodes"
import { useRoadsideDescendantCodes } from "../../hooks/utils/useRoadsideDescendantCodes"

import { RoadsideDispatchContext, RoadsideStateContext } from "./RoadsideContext.context"

type RoadsideContextProviderProps = {
    children?: React.ReactNode
}

export function RoadsideContextProvider(props: RoadsideContextProviderProps) {
    const { children } = props
    const { getParam, setParam, deleteParam } = useUrlParams()
    const { setImageCollectionCurrentYear } = useImageCollectionDispatchContext()
    const typeFilter = getParam(ROADSIDE_TYPE_URL_PARAM)
    const conditionFilter = getParam(ROADSIDE_CONDITION_URL_PARAM)
    const tagsFilter = getParam(ROADSIDE_TAGS_URL_PARAM)

    const ascendantCodes = useRoadsideAscendantCodes(typeFilter)
    const descendantCodes = useRoadsideDescendantCodes(typeFilter)

    const setFilters = useCallback((newFilters) => {
        if (newFilters?.type && typeFilter !== newFilters.type) {
            setParam(ROADSIDE_TYPE_URL_PARAM, newFilters.type)
        } else if (newFilters.type === null) {
            deleteParam(ROADSIDE_TYPE_URL_PARAM)
        }

        if (newFilters?.condition) {
            const conditionFilterArray = conditionFilter?.split(",") || []
            if (conditionFilterArray.includes(newFilters.condition)) {
                const index = conditionFilterArray.findIndex(condition => {
                    return condition === newFilters.condition
                })
                conditionFilterArray.splice(index, 1)
            } else {
                conditionFilterArray.push(newFilters.condition)
            }
            if (conditionFilterArray.length) {
                setParam(ROADSIDE_CONDITION_URL_PARAM, conditionFilterArray.join(","))
            } else {
                deleteParam(ROADSIDE_CONDITION_URL_PARAM)
            }
        }

        if (newFilters?.tags) {
            if (newFilters?.tags.length) {
                setParam(ROADSIDE_TAGS_URL_PARAM, newFilters?.tags.join(","))
            } else {
                deleteParam(ROADSIDE_TAGS_URL_PARAM)
            }
        }
    }, [conditionFilter, deleteParam, setParam, typeFilter])

    const stateValue = useMemo(() => {
        return {
            ascendantCodes,
            descendantCodes,
            filters: {
                ...(conditionFilter && {
                    conditions: conditionFilter.split(",")?.map(condition => parseInt(condition)),
                }),
                tags: tagsFilter?.split(",") || [],
                ...(typeFilter && { type: typeFilter }),
            },
        }
    }, [ascendantCodes,
        conditionFilter,
        descendantCodes,
        tagsFilter,
        typeFilter,
    ])

    const dispatchValue = useMemo(() => {
        return {
            setFilters,
        }
    }, [setFilters])

    useEffect(() => {
        const currentYear = getYear(new Date())
        setImageCollectionCurrentYear(currentYear)
    }, [setImageCollectionCurrentYear])


    return (
        <RoadsideStateContext.Provider value={stateValue}>
            <RoadsideDispatchContext.Provider value={dispatchValue}>
                {children}
            </RoadsideDispatchContext.Provider>
        </RoadsideStateContext.Provider>
    )
}

RoadsideContextProvider.propTypes = {
    children: PropTypes.node,
}
